import React, { useState, useEffect, useContext, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import Sidebar from '../components/dashboard/Sidebar';
import DashboardHeader from '../components/dashboard/DashboardHeader';
import api from '../api/api';
import { FiEdit, FiTrash2 } from 'react-icons/fi';
import { Loader } from 'lucide-react';
import EditJobModal from '../components/dashboard/EditJobModal';
import { AuthContext } from '../context/AuthContext';

const PostedJobsPage = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const hasFetchedRef = useRef(false);
  // Get user context to determine if user is a recruiter
  const { user } = useContext(AuthContext);
  const isRecruiter = user?.userType === 'recruiter';
  
  // Fetch jobs ONCE on mount
  useEffect(() => {
    const fetchJobs = async () => {
      if (!hasFetchedRef.current) {
        hasFetchedRef.current = true;
        setLoading(true);
        try {
          const response = await api.get('/jobs');
          setData(response.data);
          setError(null);
        }
        catch (err) {
          console.error('Failed to fetch jobs:', err);
          setError(err.message);
        } finally {
          setLoading(false);
        }
      }
    };
    fetchJobs();
  }, []);
  
  const refetch = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get('/jobs');
      setData(response.data);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);
  
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  
  // Determine the correct path for posting a job based on user type
  const postJobPath = isRecruiter ? '/recruiter/post-job' : '/post-job';
  
  // Filter jobs by current user
  const allJobs = data?.data?.data || data?.data?.jobs || data?.data || [];
  // Ensure allJobs is always an array
  const jobsArray = Array.isArray(allJobs) ? allJobs : [];
  const userJobs = jobsArray.filter(job => {
    if (!user?.id) return false;
    
    // For employers: show jobs posted by them
    if (user.userType === 'employer') {
      return job.postedBy === user.id;
    }
    
    // For recruiters: show jobs posted by them OR jobs assigned to them
    if (user.userType === 'recruiter') {
      return job.postedBy === user.id || job.recruiterId === user.id;
    }
    
    return false;
  });

  const handleEdit = (job) => {
    setSelectedJob(job);
    setIsModalOpen(true);
  };

  const handleDelete = async (jobId) => {
    if (window.confirm('Are you sure you want to delete this job?')) {
      try {
        setLoading(true);
        
        // Optimistically remove from UI immediately
        setData(prevData => {
          const allJobs = prevData?.data?.data || prevData?.data?.jobs || prevData?.data || [];
          const filteredJobs = Array.isArray(allJobs) ? allJobs.filter(job => job.id !== jobId) : [];
          
          // Maintain the data structure
          if (prevData?.data?.data) {
            return { ...prevData, data: { ...prevData.data, data: filteredJobs } };
          } else if (prevData?.data?.jobs) {
            return { ...prevData, data: { ...prevData.data, jobs: filteredJobs } };
          } else if (prevData?.data) {
            return { ...prevData, data: filteredJobs };
          }
          return prevData;
        });
        
        const response = await api.delete(`/jobs/${jobId}`);
        
        if (response.data.success) {
          // Refetch to ensure data consistency
          await refetch();
        } else {
          // If deletion failed, refetch to restore correct state
          await refetch();
          alert('Failed to delete job. Please try again.');
        }
      } catch (err) {
        console.error('Error deleting job:', err);
        
        // If 404, the job is already deleted - this is actually success!
        if (err.response?.status === 404) {
          // Job already deleted, just refetch to confirm
          await refetch();
          // Don't show error - it's already gone
        } else {
          // Real error - restore the job
          await refetch();
          
          // Check if it's a plan limit error or other specific error
          const errorMessage = err.response?.data?.error || 'Failed to delete job. Please try again.';
          alert(errorMessage);
        }
      } finally {
        setLoading(false);
      }
    }
  };

  const handleSave = async (updatedJob) => {
    try {
      await api.put(`/jobs/${updatedJob.id}`, updatedJob);
      refetch(); // Refetch data to update the UI
      setIsModalOpen(false);
      setSelectedJob(null);
    } catch (err) {
      console.error('Error saving job:', err);
      alert('Failed to save job. Please try again.');
    }
  };

  return (
    <div className="flex bg-white min-h-screen font-sans relative">
      <div className="fixed left-0 top-0 bottom-0 z-10">
        <Sidebar />
      </div>
      <main className="flex-1 p-8 ml-0 md:ml-24">
        {/* Assuming user data might come with the jobs data or another endpoint */}
        <DashboardHeader user={data?.user || {}} />
        
        {/* Page Header with Black Background */}
        <div className="mt-8 bg-black p-6 border border-black rounded-lg" style={{ 
          boxShadow: '3px 3px 0 rgba(255, 255, 255, 0.8)',
          transform: 'translateX(-3px) translateY(-3px)'
        }}>
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold text-white">Posted Jobs</h1>
              <p className="text-white mt-1">Manage and track your job listings</p>
            </div>
            <Link 
              to={postJobPath} 
              className="bg-white text-black font-semibold py-2.5 px-5 border border-black transition-all duration-200 flex items-center gap-2"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
              </svg>
              Post a New Job
            </Link>
          </div>
        </div>
        
        <div className="mt-6 bg-white p-8 border border-black rounded-lg" style={{ 
          boxShadow: '3px 3px 0 rgba(0, 0, 0, 0.8)',
          transform: 'translateX(-3px) translateY(-3px)'
        }}>
          <div className="overflow-x-auto">
            {loading && (
              <div className="flex flex-col justify-center items-center h-64">
                <Loader className="animate-spin text-black" size={48} />
                <p className="mt-4 text-black font-medium">Loading your job listings...</p>
              </div>
            )}
            
            {error && (
              <div className="bg-white border-l-4 border-black text-black p-6 my-4">
                <div className="flex items-center">
                  <svg className="w-8 h-8 text-black mr-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <div>
                    <h3 className="text-lg font-bold">Error</h3>
                    <p>Error: {error}</p>
                  </div>
                </div>
              </div>
            )}
            
            {userJobs && userJobs.length > 0 ? (
              <table className="w-full text-sm text-left">
                <thead>
                  <tr className="bg-black">
                    <th className="p-4 font-semibold text-white">Job Title</th>
                    <th className="p-4 font-semibold text-white">Location</th>
                    <th className="p-4 font-semibold text-white">Applicants</th>
                    <th className="p-4 font-semibold text-white">Status</th>
                    <th className="p-4 font-semibold text-white">Date Posted</th>
                    <th className="p-4 text-right font-semibold text-white">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-black">
                  {/* Show only current user's jobs */}
                  {userJobs.map((job) => (
                    <tr key={job.id} className="hover:bg-gray-100 transition-colors">
                      <td className="p-4 font-medium text-black">{job.title}</td>
                      <td className="p-4 text-black">
                        <div className="flex items-center">
                          <svg className="w-4 h-4 text-black mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                          </svg>
                          {job.location}
                        </div>
                      </td>
                      <td className="p-4">
                        <a href="#" className="px-3 py-1 bg-white text-black border border-black font-medium hover:bg-gray-100 transition-colors">
                          {job.applicants ? job.applicants.length : 0}
                        </a>
                      </td>
                      <td className="p-4">
                        <span className={`px-3 py-1.5 text-xs font-medium ${job.status === 'Active' ? 'bg-white text-black border border-black' : 'bg-black text-white border border-black'}`}>
                          {job.status}
                        </span>
                      </td>
                      <td className="p-4 text-black">{new Date(job.createdAt).toLocaleDateString()}</td>
                      <td className="p-4 text-right">
                        <div className="flex justify-end items-center gap-2">
                          <button 
                            onClick={() => handleEdit(job)} 
                            disabled={loading}
                            className="p-2 text-black hover:bg-gray-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <FiEdit size={18} />
                          </button>
                          <button 
                            onClick={() => handleDelete(job.id)} 
                            disabled={loading}
                            className="p-2 text-red-600 hover:bg-red-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <FiTrash2 size={18} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="text-center py-16 px-6">
                <div className="bg-white p-8 max-w-lg mx-auto border border-black">
                  <img 
                    src="https://cdn-icons-png.flaticon.com/512/3588/3588614.png" 
                    alt="No jobs" 
                    className="w-32 h-32 mx-auto mb-6 opacity-80"
                  />
                  <h3 className="text-xl font-bold text-black mb-3">No jobs posted yet</h3>
                  <p className="text-black mb-6">Create your first job posting to start attracting the perfect candidates for your team.</p>
                  <Link 
                    to={postJobPath} 
                    className="inline-flex items-center gap-2 bg-black text-white font-semibold py-3 px-6 border border-black transition-all duration-200"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
                    </svg>
                    Post Your First Job
                  </Link>
                </div>
              </div>
            )}
          </div>
        </div>
        {isModalOpen && (
          <EditJobModal
            job={selectedJob}
            onSave={handleSave}
            onClose={() => setIsModalOpen(false)}
          />
        )}
      </main>
    </div>
  );
};

export default PostedJobsPage;
